import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ActionsMenu from "@enerex/unified-ui-react/ActionsMenu";
import LoadingButton from "@enerex/unified-ui-react/LoadingButton";
import Table from "@enerex/unified-ui-react/Table";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useShowSnackbar from "hooks/useShowSnackbar";
import { useAuth } from "providers/CustomAuthProvider";
import * as api from "../utilityApi";
import { StateCommodityBadge } from "../CommodityIcons";
import { EMPTY_FILTERS, EMPTY_GUID, LogoPreview, buildUtilitySavePayload, csvValue, downloadCsv, getEnerexIdentifier, getIdentifier, getUserName, isUtilityActive, textOrDash } from "../utilityUtils";
import UtilityFormModal from "./UtilityFormModal";
import FilterModal from "./FilterModal";
import StateOperationModal from "../detail/StateOperationModal";
import AliasModal from "../detail/AliasModal";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((value) => ({
  value,
  label: String(value),
}));

const utilityListRequests = new Map();

const UtilityListPage = () => {
  const snackbar = useShowSnackbar();
  const { user } = useAuth();
  const currentUserName = getUserName(user);
  const navigate = useNavigate();
  const location = useLocation();
  const utilityListLocation = `${location.pathname}${location.search}`;
  const [utilities, setUtilities] = useState([]);
  const [utilityOptions, setUtilityOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [tableSorting, setTableSorting] = useState([{ id: "Name", desc: false }]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [lookupsReady, setLookupsReady] = useState(false);
  const [selectedUtility, setSelectedUtility] = useState(null);
  const [aliasStates, setAliasStates] = useState([]);
  const lookupsLoadedRef = useRef(false);
  const listRequestKeyRef = useRef("");
  const filterModalRef = useRef(null);

  const listPayload = useMemo(() => {
    let commodityId = 0;
    if (filters.Electric && filters.NatGas) commodityId = 5;
    else if (filters.Electric) commodityId = 1;
    else if (filters.NatGas) commodityId = 2;
    const activeSort = tableSorting[0] || { id: "Name", desc: false };

    return {
      Start: (page - 1) * pageSize,
      Length: pageSize,
      CurrentPage: page,
      SortColumnName: activeSort.id || "Name",
      SortColumnNameText: activeSort.id || "Name",
      SortDir: activeSort.desc ? "desc" : "asc",
      SearchValue: filters.SearchValue || "",
      CountryIdentifier: filters.CountryIdentifier || "",
      ParentUtilityExIDs: filters.ParentUtilityExIDs || [],
      StateExIDs: filters.StateExIDs || [],
      CommodityId: commodityId,
      Active: filters.Active,
      Inactive: filters.Inactive,
    };
  }, [filters, page, pageSize, tableSorting]);

  const loadUtilities = useCallback(async () => {
    const requestKey = JSON.stringify(listPayload);
    if (listRequestKeyRef.current === requestKey)
      return;

    listRequestKeyRef.current = requestKey;
    let request = utilityListRequests.get(requestKey);
    if (!request) {
      request = api.getUtilities(listPayload).finally(() => {
        utilityListRequests.delete(requestKey);
      });
      utilityListRequests.set(requestKey, request);
    }

    setLoading(true);
    try {
      const result = await request;
      setUtilities(result.rows);
      setTotal(result.total);
    } catch (error) {
      console.error("Failed to load utilities", error);
      setUtilities([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [listPayload]);

  const loadStatesForCountry = useCallback(async (countryIdentifier) => {
    const stateLookup = countryIdentifier
      ? await api.getStatesByCountry(countryIdentifier)
      : await api.getStates();
    const nextStates = Array.isArray(stateLookup) ? stateLookup : [];
    setStates(nextStates);
    return nextStates;
  }, []);

  const loadLookups = useCallback(async () => {
    if (lookupsLoadedRef.current)
      return;

    lookupsLoadedRef.current = true;
    try {
      const [utilityLookup, countryLookup] = await Promise.all([
        api.getUtilityOptions(),
        api.getCountries(),
      ]);
      const nextCountries = Array.isArray(countryLookup) ? countryLookup : [];

      setUtilityOptions(utilityLookup);
      setCountries(nextCountries);
      const usa = nextCountries.find((country) =>
        String(country.name || country.Name || country.displayName || country.DisplayName || "").toLowerCase() === "usa" ||
        String(country.code || country.Code || country.isoCode || country.IsoCode || "").toLowerCase() === "us"
      );
      const usaIdentifier = getIdentifier(usa);
      if (usaIdentifier) {
        setFilters((current) => current.CountryIdentifier
          ? current
          : { ...current, CountryIdentifier: usaIdentifier, StateExIDs: [] });
        await loadStatesForCountry(usaIdentifier);
      }
      setLookupsReady(true);
    } catch (error) {
      console.error("Failed to load utility filters", error);
      setUtilityOptions([]);
      setCountries([]);
      setStates([]);
      setLookupsReady(true);
    }
  }, [loadStatesForCountry]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (lookupsReady) loadUtilities();
  }, [loadUtilities, lookupsReady]);

  const tablePagination = useMemo(() => ({ pageIndex: page - 1, pageSize }), [page, pageSize]);

  const handleTablePaginationChange = (updater) => {
    const next = typeof updater === "function" ? updater(tablePagination) : updater;
    listRequestKeyRef.current = "";
    if (next?.pageSize !== pageSize) {
      setPageSize(next?.pageSize || pageSize);
      setPage(1);
      return;
    }

    setPage((next?.pageIndex || 0) + 1);
  };

  const handleTableSortingChange = (updater) => {
    const next = typeof updater === "function" ? updater(tableSorting) : updater;
    listRequestKeyRef.current = "";
    setTableSorting(next && next.length ? next : [{ id: "Name", desc: false }]);
    setPage(1);
  };

  const saveMaster = async (payload) => {
    setSaving(true);
    try {
      const savePayload = buildUtilitySavePayload(payload, currentUserName);
      const result = await api.saveUtility(savePayload);
      if (result === null || result === "AlreadyExist") {
        snackbar("Utility already exists", "warning");
        return;
      }
      snackbar(payload.Id ? "Utility updated successfully" : "Utility saved successfully", "success");
      setShowUtilityModal(false);
      listRequestKeyRef.current = "";
      lookupsLoadedRef.current = false;
      await Promise.all([loadLookups(), loadUtilities()]);
    } catch (error) {
      snackbar(error.message || "Failed to save utility", "error");
    } finally {
      setSaving(false);
    }
  };

  const exportUtilities = async () => {
    setSaving(true);
    try {
      const result = await api.getUtilities({
        ...listPayload,
        Start: 0,
        Length: Math.max(total, pageSize, 1000),
        CurrentPage: 1,
      });
      const header = [
        "Name",
        "Long Name",
        "Website",
        "DUNS Number",
        "Operates In and Commodity",
        "Parent",
        "Merged With",
        "Status",
      ];
      const lines = result.rows.map((utility) => {
        const statesList = utility.utilityStates || utility.UtilityStates || [];
        const operatesIn = statesList
          .map((state) => {
            const commodityId = Number(state.commodityId || state.CommodityId);
            const commodities = [
              (commodityId === 1 || commodityId === 5 || commodityId === 3) && "Electric",
              (commodityId === 2 || commodityId === 5 || commodityId === 3) && "Gas",
            ].filter(Boolean);
            return `${state.stateName || state.StateName || ""} ${commodities.join("/")}`.trim();
          })
          .filter(Boolean)
          .join("; ");

        return [
          utility.name || utility.Name,
          utility.longName || utility.LongName,
          utility.webSite || utility.WebSite,
          utility.duns || utility.Duns,
          operatesIn,
          utility.parentUtilityName || utility.ParentUtilityName,
          utility.mergedWithUtilityName || utility.MergedWithUtilityName,
          isUtilityActive(utility) ? "Active" : "Inactive",
        ].map(csvValue).join(",");
      });
      downloadCsv([header.map(csvValue).join(","), ...lines].join("\n"), "utilities.csv");
    } catch (error) {
      snackbar(error.message || "Failed to export utilities", "error");
    } finally {
      setSaving(false);
    }
  };

  const openStateModal = async (utility) => {
    setSelectedUtility(utility);
    const identifier = getEnerexIdentifier(utility);
    if (!identifier) {
      setAliasStates([]);
      setShowStateModal(true);
      return;
    }
    try {
      const utilityStates = await api.getUtilityStateOperations(identifier);
      setAliasStates(Array.isArray(utilityStates) ? utilityStates : []);
    } catch (error) {
      console.error("Failed to load utility states for state operation", error);
      setAliasStates([]);
    }
    setShowStateModal(true);
  };

  const openAliasModal = async (utility) => {
    setSelectedUtility(utility);
    const identifier = getEnerexIdentifier(utility);
    if (!identifier) {
      setAliasStates([]);
      setShowAliasModal(true);
      return;
    }
    try {
      const utilityStates = await api.getUtilityStateOperations(identifier);
      setAliasStates(Array.isArray(utilityStates) ? utilityStates : []);
    } catch (error) {
      console.error("Failed to load utility states for alias", error);
      setAliasStates([]);
    }
    setShowAliasModal(true);
  };

  const saveStateOperation = async (payload) => {
    const identifier = getEnerexIdentifier(selectedUtility);
    if (!identifier) return;
    setSaving(true);
    try {
      const commodityIds = (payload.CommodityIds?.length ? payload.CommodityIds : [payload.CommodityId])
        .map((commodityId) => Number(commodityId))
        .filter((commodityId) => commodityId === 1 || commodityId === 2);
      const primaryCommodityId = commodityIds.length === 2 ? 3 : commodityIds[0];
      if (!commodityIds.length) {
        snackbar("Select at least one commodity", "warning");
        return;
      }
      const result = await api.saveUtilityStateOperation({
        StateIds: payload.StateId ? [Number(payload.StateId)] : [],
        CommodityIds: commodityIds,
        CommodityMappings: commodityIds.map((commodityId) => ({ Id: "0", CommodityId: commodityId })),
        UtilityId: 0,
        CommodityId: primaryCommodityId,
        Electric: commodityIds.includes(1),
        Gas: commodityIds.includes(2),
        NatGas: commodityIds.includes(2),
        StateId: String(payload.StateId || ""),
        StateName:
          payload.SelectedState?.stateName ||
          payload.SelectedState?.StateName ||
          payload.StateName ||
          "",
        UtilityName: selectedUtility?.name || selectedUtility?.Name || "",
        EnerexUtilityId: getEnerexIdentifier(selectedUtility) || EMPTY_GUID,
        EnerexStateId: payload.SelectedState?.enerexIdentifier || payload.SelectedState?.EnerexIdentifier || EMPTY_GUID,
        EnerexUtilityStateId: EMPTY_GUID,
        UserName: currentUserName,
        ServiceReferenceNumberRequired: Boolean(payload.ServiceReferenceNumberRequired),
        ServiceReferenceNumberDescription: payload.ServiceReferenceNumberDescription || null,
        KeyNameRequired: Boolean(payload.KeyNameRequired),
        KeyNameDescription: payload.KeyNameDescription ?? "",
        EnrollmentLeadTime: payload.EnrollmentLeadTime ?? "",
      });
      if (!result || (Array.isArray(result) && result.length === 0)) {
        snackbar("State of operation already existed", "warning");
        return;
      }
      snackbar("Utility state operation saved successfully", "success");
      setShowStateModal(false);
      listRequestKeyRef.current = "";
      await loadUtilities();
    } catch (error) {
      snackbar(error.message || "Failed to save state operation", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveAlias = async (payload) => {
    const identifier = getEnerexIdentifier(selectedUtility);
    if (!identifier) return;
    setSaving(true);
    try {
      const result = await api.createUtilityAlias({
        Id: 0,
        Name: payload.Alias,
        Identifier: identifier,
        StateId: payload.StateId,
        StateName: payload.StateName,
        CheckValidation: true,
        UserName: currentUserName,
      });
      if (!result || result === "AlreadyExist") {
        snackbar("Alias was not added. It may already exist.", "warning");
        return;
      }
      snackbar("Utility alias saved successfully", "success");
      setShowAliasModal(false);
      listRequestKeyRef.current = "";
      await loadUtilities();
    } catch (error) {
      snackbar(error.message || "Failed to save alias", "error");
    } finally {
      setSaving(false);
    }
  };

  const setUtilityStatus = async (utility, activate) => {
    const identifier = getEnerexIdentifier(utility);
    if (!identifier) return;
    setSaving(true);
    try {
      if (activate) {
        await api.activateUtility(identifier, currentUserName);
      } else {
        await api.deactivateUtility(identifier, currentUserName);
      }
      snackbar(`Utility ${activate ? "activated" : "deactivated"} successfully`, "success");
      listRequestKeyRef.current = "";
      lookupsLoadedRef.current = false;
      await Promise.all([loadLookups(), loadUtilities()]);
    } catch (error) {
      snackbar(error.message || "Failed to update utility status", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderActionMenu = (utility) => {
    const active = isUtilityActive(utility);
    const identifier = getEnerexIdentifier(utility);

    return (
      <div className="position-static" onClick={(event) => event.stopPropagation()}>
        <ActionsMenu align="end">
          {active ? (
            <>
              <ActionsMenu.ActionItem onClick={() => openStateModal(utility)} className="d-flex align-items-center gap-2">
                <i className="uil uil-plus-square" />
                Add State of operations
              </ActionsMenu.ActionItem>
              <ActionsMenu.ActionItem onClick={() => openAliasModal(utility)} className="d-flex align-items-center gap-2">
                <i className="uil uil-plus" />
                Add Alias
              </ActionsMenu.ActionItem>
              <ActionsMenu.ActionItem onClick={() => navigate(`/utilities/${identifier}`, { state: { utilityListLocation } })} className="d-flex align-items-center gap-2">
                <i className="uil uil-eye" />
                Show Details
              </ActionsMenu.ActionItem>
              <ActionsMenu.ActionItem onClick={() => setUtilityStatus(utility, false)} className="d-flex align-items-center gap-2 text-danger">
                <i className="uil uil-trash-alt" />
                Deactivate
              </ActionsMenu.ActionItem>
            </>
          ) : (
            <ActionsMenu.ActionItem onClick={() => setUtilityStatus(utility, true)} className="d-flex align-items-center gap-2">
              <i className="uil uil-check-circle" />
              Activate
            </ActionsMenu.ActionItem>
          )}
        </ActionsMenu>
      </div>
    );
  };

  const utilityColumns = [
    {
      id: "Name",
      header: <span style={{ display: "block", marginLeft: "72px" }}>Name</span>,
      accessorFn: (utility) => utility.name || utility.Name || "",
      cell: ({ row }) => {
        const utility = row.original;
        const identifier = getEnerexIdentifier(utility);
        const name = textOrDash(utility.name || utility.Name);
        const active = isUtilityActive(utility);
        const status = active ? "Active" : "Inactive";

        return (
          <div className="d-flex align-items-center gap-2">
            <span
              title={status}
              aria-label={status}
              style={{
                width: "8px",
                height: "8px",
                display: "inline-block",
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: active ? "#28a745" : "#dc3545",
              }}
            />
            <span
              style={{
                width: "36px",
                height: "28px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <LogoPreview utility={utility} />
            </span>
            {active ? (
              <Link className="link-color fw-semibold" to={`/utilities/${identifier}`} state={{ utilityListLocation }}>
                {name}
              </Link>
            ) : name}
          </div>
        );
      },
    },
    {
      id: "LongName",
      header: "Long Name",
      accessorFn: (utility) => utility.longName || utility.LongName || "",
      cell: ({ row }) => textOrDash(row.original.longName || row.original.LongName),
    },
    {
      id: "WebSite",
      header: "Website",
      accessorFn: (utility) => utility.webSite || utility.WebSite || "",
      enableSorting: false,
      cell: ({ row }) => {
        const website = row.original.webSite || row.original.WebSite;
        return website ? <a href={website} target="_blank" rel="noreferrer">{website}</a> : textOrDash();
      },
    },
    {
      id: "Duns",
      header: "DUNS",
      accessorFn: (utility) => utility.duns || utility.Duns || "",
      enableSorting: false,
      cell: ({ row }) => textOrDash(row.original.duns || row.original.Duns),
    },
    {
      id: "OperatesIn",
      header: "Operates In",
      enableSorting: false,
      cell: ({ row }) => {
        const statesList = row.original.utilityStates || row.original.UtilityStates || [];
        if (!statesList.length) return textOrDash();

        return (
          <div className="d-flex flex-wrap gap-1">
            {statesList.map((state, index) => (
              <StateCommodityBadge
                key={`${state.stateName || state.StateName || index}-${state.commodityId || state.CommodityId || index}`}
                state={state}
              />
            ))}
          </div>
        );
      },
    },
    {
      id: "Parent",
      header: "Parent",
      enableSorting: false,
      cell: ({ row }) => textOrDash(row.original.parentUtilityName || row.original.ParentUtilityName),
    },
    {
      id: "MergedWith",
      header: "Merged With",
      enableSorting: false,
      cell: ({ row }) => textOrDash(row.original.mergedWithUtilityName || row.original.MergedWithUtilityName),
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => renderActionMenu(row.original),
    },
  ];

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end ms-auto">
          <LoadingButton size="sm" variant="primary" onClick={() => filterModalRef.current?.handleReset()}>
            Reset
          </LoadingButton>
          <LoadingButton size="sm" variant="primary" onClick={() => filterModalRef.current?.handleApply()}>
            Apply
          </LoadingButton>
          <LoadingButton size="sm" variant="primary" disabled={saving || loading} onClick={exportUtilities}>
            Export
          </LoadingButton>
          <LoadingButton size="sm" variant="primary" onClick={() => setShowUtilityModal(true)}>
            <i className="uil uil-plus" /> Add Utility
          </LoadingButton>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-2">
            <FilterModal
              ref={filterModalRef}
              show={showFilters}
              onHide={() => setShowFilters(false)}
              onToggle={() => setShowFilters((current) => !current)}
              filters={filters}
              utilityOptions={utilityOptions}
              countries={countries}
              states={states}
              onCountryChange={loadStatesForCountry}
              onApply={(nextFilters) => {
                listRequestKeyRef.current = "";
                setFilters(nextFilters);
                setPage(1);
                setShowFilters(false);
              }}
            />
          </div>

          <Table
            columns={utilityColumns}
            data={utilities}
            loading={loading}
            enableSearch={false}
            enablePagination
            enableHideColumns={false}
            enableSorting
            enableFilters={false}
            enableSelection={false}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            pageSizes={PAGE_SIZE_OPTIONS.map((option) => option.value)}
            pagination={tablePagination}
            onPaginationChange={handleTablePaginationChange}
            sorting={tableSorting}
            onSortingChange={handleTableSortingChange}
            total={total}
            noDataMessage="No utilities found."
          />
        </div>
      </div>

      {/* Modals */}
      <UtilityFormModal
        show={showUtilityModal}
        onHide={() =>
          setShowUtilityModal(false)
        }
        onSave={saveMaster}
        utilityOptions={utilityOptions}
        saving={saving}
      />

      <StateOperationModal
        show={showStateModal}
        onHide={() =>
          setShowStateModal(false)
        }
        onSave={saveStateOperation}
        countries={countries}
        existingStates={aliasStates}
        onCountryChange={loadStatesForCountry}
        saving={saving}
      />

      <AliasModal
        show={showAliasModal}
        onHide={() =>
          setShowAliasModal(false)
        }
        onSave={saveAlias}
        states={
          aliasStates.length
            ? aliasStates
            : states
        }
        stateLookup={states}
        saving={saving}
      />
    </div>
  );
};

export default UtilityListPage;
